import { useCallback, useEffect, useRef, useState } from 'react'

import { FeatureFlagName, IntercomService, TutorialService } from '@api'
import { useUnit } from 'effector-react'

import { LocalStorageKeys } from '@/consts/localStorage'
import { $creators, $isCreatorProcessCompleted } from '@/models/creators'
import { $availableFeatures } from '@/models/feature-flags'
import { openFx } from '@/models/router'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'
import { isFeatureAvailable as isFeatureAvailableUtil } from '@/utils/featureFlags.ts'

import { $helpDeskUrl } from './overviewLinks'

export const usePageGuide = () => {
  const articleRef = useRef<HTMLDivElement>(null)
  const info = useUnit($helpDeskUrl)
  const creators = useUnit($creators)
  const isCreatorsLoaded = useUnit($isCreatorProcessCompleted)
  const availableFeatures = useUnit($availableFeatures)
  const [body, setBody] = useState<string | null>(null)
  const [activeArticle, setActiveArticle] = useState<{
    articleId: number
    helpDeskLinkName: string
  } | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const handleOpenGuide = useCallback(
    async (articleId: number | null, shouldFetch: boolean) => {
      if (!articleId) {
        return
      }

      setIsOpen(true)
      if (!shouldFetch) {
        return
      }

      const articleBody = await IntercomService.getArticleById({
        id: String(articleId),
      })

      setBody(articleBody)
      // reset scroll for new article
      setTimeout(() => {
        articleRef.current?.scrollTo({ top: 0, behavior: 'instant' })
      }, 0)
    },
    [],
  )

  const handleToggle = useCallback(async () => {
    if (isOpen) {
      setIsOpen(false)
    } else {
      trackFeatureUsage(TrackedFeatures.HelpDesk)
      handleOpenGuide(activeArticle?.articleId ?? null, !body)
    }
  }, [isOpen, handleOpenGuide, activeArticle, body])

  const checkTrigger = useCallback(
    async ({
      helpDeskLinkName,
      articleId,
    }: {
      helpDeskLinkName: string
      articleId: number
    }) => {
      const isFeatureAvailable = isFeatureAvailableUtil(
        availableFeatures,
        FeatureFlagName.INTERCOM_ARTICLES,
      )
      const isUserFlowFeatureAvailable = isFeatureAvailableUtil(
        availableFeatures,
        FeatureFlagName.USER_FLOW,
      )
      const viewed = JSON.parse(
        localStorage.getItem(LocalStorageKeys.HELP_DESK_ARTICLES_VIEWED) ??
          '{}',
      )

      if (
        isUserFlowFeatureAvailable ||
        !isFeatureAvailable ||
        !creators?.length ||
        viewed[helpDeskLinkName] ||
        viewed.isHidden
      ) {
        return
      }
      try {
        const isStepCompleted = await TutorialService.getTutorialStepCompleted({
          step: helpDeskLinkName,
        })

        if (!isStepCompleted) {
          handleOpenGuide(articleId, true)

          TutorialService.completeTutorialStep({
            step: helpDeskLinkName,
          })
        }

        // to reduce the number of requests to the server
        localStorage.setItem(
          LocalStorageKeys.HELP_DESK_ARTICLES_VIEWED,
          JSON.stringify({
            ...viewed,
            [helpDeskLinkName]: true,
          }),
        )
      } catch (error) {
        console.error('Error saving tutorial step:', error)
      }
    },
    [availableFeatures, creators, handleOpenGuide],
  )

  const handleFullGuideClick = useCallback(() => {
    if (info?.helpDeskLink) {
      openFx(info.helpDeskLink)
    }
  }, [info])

  useEffect(() => {
    setIsOpen(false)

    // Smooth transition to the new article
    const timeout = setTimeout(() => {
      setBody(null)
      setActiveArticle(info)

      if (!info?.articleId || !isCreatorsLoaded) {
        return
      }

      checkTrigger(info)
    }, 300)

    return () => {
      clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.articleId, isCreatorsLoaded])

  return {
    articleRef,
    isOpen,
    body,
    activeArticle,
    onToggle: handleToggle,
    onClose: () => setIsOpen(false),
    onOpenFull: handleFullGuideClick,
    title: activeArticle?.helpDeskLinkName || info?.helpDeskLinkName,
  }
}
